<template>
  <div>
    <SelectRolloverModal
      :open="isSelectRolloverModalOpen"
      @close-modal="isSelectRolloverModalOpen = false"
    />
    <div class="flex flex-col gap-[2.5rem] lg:gap-[3.5rem]">
      <p
        v-if="offeringRateRange && experiment.isInTestGroup.value"
        class="text-base font-inter text-strong -mt-6 lg:-mt-10 -mb-4 lg:-mb-6"
      >
        Get started with our {{ offeringRateRange }} annual interest rate bond.
      </p>

      <div>
        <div class="bg-container-bg p-4 rounded-xl w-full lg:w-1/2">
          <label class="block text-base font-medium text-strong mb-3 font-inter">
            Enter amount of investment
          </label>
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <span class="text-7xl font-medium text-strongest">$</span>
              <input
                ref="inputRef"
                type="text"
                inputmode="numeric"
                :value="displayValue"
                @input="handleInput"
                @blur="handleBlur"
                class="bg-transparent text-7xl font-medium text-strongest outline-none w-full placeholder:text-7xl placeholder:font-medium placeholder:text-strongest"
                placeholder="0.00"
              />
            </div>
          </div>

          <!-- Max available text - only show when a rollover is selected -->
          <p v-if="purchaseFlowStore.selected_rollover" class="mt-3 font-inter text-strong">
            Max available to reinvest:
            {{ formatCurrency(purchaseFlowStore.selected_rollover.rollover_available) }}
          </p>
        </div>
        <div
          class="bg-container-bg py-1 px-2 rounded-lg w-fit mt-2 flex text-sm items-center text-strong font-inter font-semibold"
        >
          <Icon name="Asterisk" class="-skew-x-12 text-info h-4 w-4 mr-1" /> Amounts must be in
          increments of $1,000
        </div>
        <div
          class="font-inter text-xs font-stretch-normal not-italic leading-none tracking-normal text-left text-light h-4 mt-1"
        >
          {{ formatCurrency(purchaseFlowStore.selected_offering.minimum_investment) }} Minimum
          investment required for this offering
        </div>
      </div>

      <RolloverTable v-if="has_rollovers_available && purchaseFlowStore.selected_rollover" />
      <div v-else-if="has_rollovers_available && !purchaseFlowStore.selected_rollover">
        <h3 class="text-xl lg:text-[28px] font-semibold text-strongest mb-3">
          Reinvestment available
        </h3>
        <PaperCard :has_header="false">
          <template #body>
            <div class="flex flex-col lg:flex-row lg:items-center justify-between md:px-4 md:py-1">
              <div>
                <p class="text-2xl lg:text-[28px] font-medium text-strongest">
                  {{ formatCurrency(total_rollover_available) }} available for reinvestment
                </p>
                <p class="text-sm lg:text-base font-inter text-light">
                  Across {{ investments_for_rollover.length }} investments
                </p>
              </div>
              <Button
                variant="primary-grey"
                size="lg-sm"
                @click="isSelectRolloverModalOpen = true"
                class="mt-6 lg:mt-0 w-fit"
              >
                <template #label>
                  <p class="text-base lg:text-lg font-inter">View Available Investments</p>
                </template>
              </Button>
            </div>
          </template>
        </PaperCard>
      </div>
      <!-- Help section — only shown for users in the new-experience group -->
      <div v-if="experiment.isInTestGroup.value" class="flex flex-col gap-3">
        <div class="flex items-center gap-3">
          <div
            class="h-10 w-10 min-w-10 rounded-full bg-outline-dividers flex items-center justify-center shrink-0"
          >
            <Icon
              name="MessageCircleQuestionMark"
              class="w-5 h-5 text-cta-tertiary-outline-icon"
              :stroke-width="2.5"
            />
          </div>
          <div>
            <p class="font-semibold text-strongest font-inter">Have questions about investing?</p>
            <p class="text-sm text-light font-medium font-inter">Reach out to us!</p>
          </div>
        </div>

        <div class="flex flex-col gap-2 pl-[48px]">
          <Button @click="openCalendly" label size="md" variant="primary-secondary" class="w-min">
            <template #icon><Icon name="Handshake" class="w-5 h-5" /></template>
            <template #label>
              <span class="hidden lg:inline font-inter"
                >Meet with a Licensed Investment Professional</span
              >
              <span class="lg:hidden font-inter">Meet a Licensed Investment Pro</span>
            </template>
          </Button>
          <Button @click="callUs" label icon size="md" variant="paper-primary" class="w-min">
            <template #icon><Icon name="Phone" class="w-4 h-4" :stroke-width="2.5" /></template>
            <template #label>Call us at 855-732-6395</template>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch } from "vue";
import { Button, Icon, PaperCard } from "@/components";
import { usePurchaseFlowStore, useUIStore, useDataStore, useCrudStore } from "@/stores";
import { commaSeparateThousands } from "@/utilities";
import { useStripeCheckout } from "@/composables/useStripeCheckout";
import RolloverTable from "./PurchaseFlowUtilities/RolloverTable.vue";
import SelectRolloverModal from "./PurchaseFlowUtilities/SelectRolloverModal.vue";
import { useOfferingExperiment } from "@/composables/useOfferingExperiment";

const purchaseFlowStore = usePurchaseFlowStore();
const UIStore = useUIStore();
const dataStore = useDataStore();
const crudStore = useCrudStore();
const stripeCheckout = useStripeCheckout();
const experiment = useOfferingExperiment();

const offeringRateRange = computed(() => {
  const type = purchaseFlowStore.selected_offering?.type;
  if (!type) return null;
  const rates = dataStore.offerings
    .filter((o) => {
      if (o.is_short_term || o.type !== type) return false;
      if (type === "Reg D") return o.tier === purchaseFlowStore.regDTier;
      return true;
    })
    .map((o) => o.interest_rate);
  if (!rates.length) return null;
  const min = Math.min(...rates);
  const max = Math.max(...rates);
  return min === max ? `${min}%` : `${min}-${max}%`;
});

const { has_rollovers_available, total_rollover_available, investments_for_rollover } =
  purchaseFlowStore.rolloverGetter;

const localValue = ref(null);
const inputRef = ref(null);
const isSelectRolloverModalOpen = ref(false);

const displayValue = computed(() => {
  if (localValue.value !== null) {
    return localValue.value;
  }

  const storeValue = purchaseFlowStore.current_investment.amount;

  if (typeof storeValue === "number") {
    return commaSeparateThousands(storeValue);
  }

  return storeValue || "";
});

const maximumAmountForRollover = computed(() => {
  if (!purchaseFlowStore.selected_rollover) return null;
  return Math.floor(purchaseFlowStore.selected_rollover.rollover_available / 1000) * 1000;
});

const minimumInvestment = computed(() => {
  const offering = purchaseFlowStore.selected_offering;

  // Rollovers/reinvestments are always $1k
  if (purchaseFlowStore.selected_rollover) return 1000;

  // Short-term offerings are always $1k
  if (offering.is_short_term) return purchaseFlowStore.selected_offering.minimum_investment;

  // Returning S1 investors get $1k
  if (offering.type === "S1" && purchaseFlowStore.signedInvestmentStatus.has_signed_s1) return 1000;

  // Returning Reg D investors get $1k
  if (offering.type === "Reg D" && purchaseFlowStore.signedInvestmentStatus.has_signed_regd)
    return 1000;

  // Everything else uses the offering's own minimum
  return offering.minimum_investment;
});

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function handleInput(event) {
  const rawInput = event.target.value;
  const numericOnly = rawInput.replace(/[^0-9]/g, "");
  const numericValue = parseInt(numericOnly) || 0;
  const formatted = numericValue ? commaSeparateThousands(numericValue) : "";

  localValue.value = formatted;

  nextTick(() => {
    if (inputRef.value) {
      inputRef.value.value = formatted;
    }
  });
}

function handleBlur() {
  const rawValue = parseInt((localValue.value || "").replace(/,/g, ""), 10) || 0;

  if (rawValue > 0) {
    let rounded = Math.max(minimumInvestment.value, Math.ceil(rawValue / 1000) * 1000);

    // Rounding up must never push a reinvestment past what's actually available —
    // that would leave the investor stuck on an amount Continue refuses to accept.
    if (maximumAmountForRollover.value) {
      rounded = Math.min(rounded, maximumAmountForRollover.value);
    }

    const currentAmount = Number(
      String(purchaseFlowStore.current_investment.amount).replace(/,/g, "")
    );

    if (rounded !== currentAmount) {
      const inv = purchaseFlowStore.current_investment;
      const piId = String(inv.funding_account ?? "").startsWith("pi_") ? inv.funding_account : null;
      if (piId) {
        stripeCheckout.cancelPaymentIntent(piId);

        inv.funding_account = null;
      }
    }

    purchaseFlowStore.current_investment.amount = rounded;
  }
  localValue.value = null;
}

function openCalendly() {
  let log = {
    step: "Experiment CTA",
    substep: "Meet with a Licensed Investment Professional",
    status: "Navigated",
    message: "",
    url: "https://calendly.com/d/cxfk-wk9-57b/connect-with-phoenix-energy",
    validation_details: null,
  };
  log = dataStore.setDefaultFields(log, "investoractivity");
  crudStore.insert("investoractivity", [log]);
  window.open(
    "https://calendly.com/d/cxfk-wk9-57b/connect-with-phoenix-energy?utm_source=FP&utm_medium=portal&utm_campaign=support",
    "_blank"
  );
}

function callUs() {
  let log = {
    step: "Experiment CTA",
    substep: "Call Us",
    status: "Navigated",
    message: "",
    url: "tel:8557326395",
    validation_details: null,
  };
  log = dataStore.setDefaultFields(log, "investoractivity");
  crudStore.insert("investoractivity", [log]);
  window.location.href = "tel:8557326395";
}

defineExpose({
  async validate() {
    const amount = purchaseFlowStore.current_investment.amount;

    if (!amount || amount < 1000) {
      UIStore.animateNotificationAlert({
        type: "error",
        icon: "InfoWarning",
        message: "Please enter an investment amount of at least $1,000.",
      });
      return { proceed: false, error_messages: ["Amount must be at least $1,000"] };
    }

    if (amount < minimumInvestment.value) {
      UIStore.animateNotificationAlert({
        type: "error",
        icon: "InfoWarning",
        message: `Minimum investment for this offering is ${formatCurrency(minimumInvestment.value)}.`,
      });
      return {
        proceed: false,
        error_messages: [
          `Minimum investment for this offering is ${formatCurrency(minimumInvestment.value)}`,
        ],
      };
    }

    if (maximumAmountForRollover.value && amount > maximumAmountForRollover.value) {
      UIStore.animateNotificationAlert({
        type: "error",
        icon: "InfoWarning",
        message: `Reinvestment amount cannot exceed ${formatCurrency(maximumAmountForRollover.value)} (available from selected reinvestment).`,
      });
      return {
        proceed: false,
        error_messages: [
          `Reinvestment amount cannot exceed ${formatCurrency(maximumAmountForRollover.value)}`,
        ],
      };
    }

    return { proceed: true };
  },
  afterContinue: async () => await purchaseFlowStore.saveInvestmentProgress(),
});
</script>
